import { useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { unitsApiKeyStore } from '../../api/units-api-key-store';
import { usersApiKeyStore } from '../../api/users-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { servicesApiKeyStore } from '../../api/masters/services-api-key-store';
import { workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { DateFormat, FormDateField } from '../../components/hook-form/form-date.field.component';
import { FormAutoCompleteSelectDynamic } from '../../components/hook-form/form-autocomplete-select.dynamic.component';
import { FormSelect } from '../../components/hook-form/form-select.component';
import { FormCheckbox } from '../../components/hook-form/form-checkbox.component';
import dayjs from 'dayjs';
import { FormAutoCompleteMultiSelectDynamic } from '../query-builder/hooks-form-querybuilder/form-autocomplete-multi-select.dynamic.component';

interface WorkOrderFilters {
  onShowAllWorkOrder: () => void;
}

export const WorkOrderFilters = ({ onShowAllWorkOrder }: WorkOrderFilters) => {
  const isMultiSelectReportEnabled = process.env.REACT_APP_MULTI_SELECT_REPORT_FLAG === 'true';
  const { setValue } = useFormContext();
  const [fromDateName, setFromDateName] = useState<string>();
  const [toDateName, setToDateName] = useState<string>();
  const [officeDisabled, setOfficeDisabled] = useState<boolean>(true);
  const [projectDisabled, setProjectDisabled] = useState<boolean>(true);
  const [unitDisabled, setUnitDisabled] = useState<boolean>(true);

  const [disabledFromData, setDisabledFromDate] = useState<boolean>(false);
  const [disabledToDate, setDisabledToDate] = useState<boolean>(false);

  useEffect(() => {
    // Set default values for dateCriteria, fromDate, and toDate
    if (isMultiSelectReportEnabled) {
      setValue('dateCriteria', [{ dateCriteriaValue: 'created', dateCriteria: 'Created' }]);
    } else {
      setValue('dateCriteria', { dateCriteriaValue: 'created', dateCriteria: 'Created' });
    }
    setValue('fromDate', dayjs().subtract(2, 'week').format(DateFormat.SIMPLE_DATE));
    setValue('toDate', dayjs().format(DateFormat.SIMPLE_DATE));
    setDisabledFromDate(true);
    setDisabledToDate(true);
  }, [setValue, isMultiSelectReportEnabled]);

  const setActionNull = () => {
    setValue('action', '');
  };

  const validateDateField = (status: any) => {
    setDisabledFromDate(!!status);
    setDisabledToDate(false);
    setValue('fromDate', '');
    setValue('toDate', '');
  };

  const handleStatusChange = (value: any) => {
    validateDateField(value);
    if (value?.dateCriteria !== undefined) {
      setFromDateName(`${value?.dateCriteria} From`);
      setToDateName(`${value?.dateCriteria} To`);
    } else {
      setFromDateName(`Date From`);
      setToDateName(`Date To`);
      setValue('dateCriteria', { dateCriteriaValue: 'created', dateCriteria: 'Created' });
      setValue('fromDate', dayjs().subtract(2, 'week').format(DateFormat.SIMPLE_DATE));
      setValue('toDate', dayjs().format(DateFormat.SIMPLE_DATE));
      setDisabledFromDate(true);
      setDisabledToDate(true);
    }
    setActionNull();
  };

  const handleCustomerChange = (value: any) => {
    setOfficeDisabled(!value);
    setProjectDisabled(true);
    setUnitDisabled(true);
    setValue('office', { officeID: '', officeName: '' });
    setValue('project', { projectID: '', projectName: '' });
    setValue('unit', { unitID: '', unitName: '' });
    setActionNull();
  };

  const handleOfficeChange = (value: any) => {
    setProjectDisabled(!value);
    setUnitDisabled(true);
    setValue('project', { projectID: '', projectName: '' });
    setValue('unit', { unitID: '', unitName: '' });
  };

  const handleProjectChange = (value: any) => {
    setUnitDisabled(!value);
    setValue('unit', { unitID: '', unitName: '' });
  };

  const handleFromDate = (date: any) => {
    setDisabledToDate(!!date);
    setValue('toDate', '');
  };

  const handleActionChange = (event: any) => {
    setValue('customer', { customerID: '', customerName: '' });
    setValue('user', { userID: '', fullName: '' });
    setValue('service', { serviceID: '', serviceName: '' });
    setValue('dateCriteria', { dateCriteriaValue: '', dateCriteria: '' });
    setValue('workOrderStatus', { workOrderStatusID: '', workOrderStatus: '' });
    setOfficeDisabled(true);
    setProjectDisabled(true);
    setUnitDisabled(true);
    setValue('office', { officeID: '', officeName: '' });
    setValue('project', { projectID: '', projectName: '' });
    setValue('unit', { unitID: '', unitName: '' });
    validateDateField(false);
    if (event.target.checked) onShowAllWorkOrder();
  };

  const handleDateCriteria = (value: any) => {
    if (value?.length == 0) {
      setValue('dateCriteria', [{ dateCriteriaValue: 'created', dateCriteria: 'Created' }]);
      setValue('fromDate', dayjs().subtract(2, 'week').format(DateFormat.SIMPLE_DATE));
      setValue('toDate', dayjs().format(DateFormat.SIMPLE_DATE));
      setDisabledFromDate(true);
      setDisabledToDate(true);
    }
  }

  const options = [
    {
      value: 'active',
      label: 'Active',
      isDefault: false,
    },
    {
      value: 'inactive',
      label: 'Inactive',
      isDefault: false,
    },
  ];

  return (
    <Grid container spacing={1}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Grid container spacing={1}>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="customer"
              label="Customer"
              onChange={handleCustomerChange}
              getQueryKeyConfig={() => customersApiKeyStore.getCustomers()}
              optionsValueKey="customerID"
              optionsLabelKey="customerName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="office"
              label="Offices"
              disabled={officeDisabled}
              watchKeys={['customer']}
              onChange={handleOfficeChange}
              getQueryKeyConfig={(inputData: any) =>
                officeApiKeyStore.getCustomerOffices(inputData.customer?.customerID)
              }
              optionsValueKey="officeID"
              optionsLabelKey="officeName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="project"
              label="Projects"
              disabled={projectDisabled}
              watchKeys={['office']}
              onChange={handleProjectChange}
              getQueryKeyConfig={(inputData: any) => projectsApiKeyStore.getOfficeProjects(inputData.office?.officeID)}
              optionsValueKey="projectID"
              optionsLabelKey="projectName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="unit"
              label="Lot/Unit"
              disabled={unitDisabled}
              watchKeys={['project']}
              getQueryKeyConfig={(inputData: any) => unitsApiKeyStore.getProjectUnits(inputData.project?.projectID)}
              optionsValueKey="unitID"
              optionsLabelKey="unitName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="user"
              label="Inspectors"
              onChange={setActionNull}
              getQueryKeyConfig={() => usersApiKeyStore.getUsersByRole('ins')}
              optionsValueKey="userID"
              optionsLabelKey="fullName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="service"
              label="Service"
              onChange={setActionNull}
              getQueryKeyConfig={() => servicesApiKeyStore.getServices()}
              optionsValueKey="serviceID"
              optionsLabelKey="serviceName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            {
              isMultiSelectReportEnabled ?
                <FormAutoCompleteMultiSelectDynamic
                  name="dateCriteria"
                  label="Date Criteria"
                  onChange={handleDateCriteria}
                  getQueryKeyConfig={() => workorderReportApiKeyStore.getWorkOrderReportDateCriteria()}
                  optionsLabelKey="dateCriteria"
                  optionsValueKey="dateCriteriaValue"
                /> :
                <FormAutoCompleteSelectDynamic
                  name="dateCriteria"
                  label="Date Criteria"
                  onChange={handleStatusChange}
                  getQueryKeyConfig={() => workorderReportApiKeyStore.getWorkOrderReportDateCriteria()}
                  optionsValueKey="dateCriteriaValue"
                  optionsLabelKey="dateCriteria"
                />
            }
          </Grid>
          <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
            <FormDateField
              label={fromDateName ? fromDateName : 'Date From'}
              name="fromDate"
              onChange={handleFromDate}
              disabled={!disabledFromData}
              format={DateFormat.SIMPLE_DATE}
            />
          </Grid>
          <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
            <FormDateField
              label={toDateName ? toDateName : 'Date To'}
              name="toDate"
              disabled={!disabledToDate}
              format={DateFormat.SIMPLE_DATE}
              dateLimits={{
                minDateEnabled: true,
                minDateValue: 'fromDate',
                // maxDateEnabled: true
              }}
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="projectStatus"
              label="Project Status"
              dataItems={[
                { statusValue: 'all', statusLabel: 'All' },
                { statusValue: 'active', statusLabel: 'Active' },
                { statusValue: 'archived', statusLabel: 'Archived' }]}
              optionsValueKey="statusValue"
              optionsLabelKey="statusLabel"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            {
              isMultiSelectReportEnabled ?
                <FormAutoCompleteMultiSelectDynamic
                  name="workOrderStatusID"
                  label="Status"
                  getQueryKeyConfig={() => workOrdersApiKeyStore.getWorkOrderStatus('inspection')}
                  optionsLabelKey="workOrderStatus"
                  optionsValueKey="workOrderStatusID"
                />
                :
                <FormAutoCompleteSelectDynamic
                  name="workOrderStatus"
                  label="Status"
                  onChange={handleStatusChange}
                  getQueryKeyConfig={() => workOrdersApiKeyStore.getWorkOrderStatus('inspection')}
                  optionsValueKey="workOrderStatusID"
                  optionsLabelKey="workOrderStatus"
                />
            }
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="workOrderActiveStatus"
              label="Active/Inactive Work Order Status"
              dataItems={[
                { statusValue: 'all', statusLabel: 'All' },
                { statusValue: 'active', statusLabel: 'Active' },
                { statusValue: 'inactive', statusLabel: 'Inactive' }]}
              optionsValueKey="statusValue"
              optionsLabelKey="statusLabel"
            />
          </Grid>
          {/* <Grid item xl={1.8} lg={1.8} sm={6} xs={6}>
            <FormSelect label="Action" name="action" onChange={handleActionChange} options={options} />
          </Grid> */}
          {/* {showExternalDate && (
            <>
              <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
                <FormDateField label={'Scheduled Date From'} name="scheduledDateFrom" format={DateFormat.SIMPLE_DATE} />
              </Grid>
              <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
                <FormDateField label={'Scheduled Date To'} name="scheduledDateTo" format={DateFormat.SIMPLE_DATE} />
              </Grid>
            </>
          )} */}
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="center">
              <Button type="submit" title="Filter" variant="contained" startIcon={<FilterAltOutlinedIcon />}>
                Filter
              </Button>
              {/* <div style={{ paddingLeft: 15 }}>
                <FormCheckbox label="Show All" name="action" onChange={handleActionChange} />
              </div> */}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
